// The engine's own data boundary for a run.
//
// A run's createdAt is when the run EXECUTED, which is typically several
// days after the data it was given ends. sales_order_master blends actuals
// with forecast on the same columns, so treating createdAt as the actuals
// cutoff silently counts forecast as shipped: for Cora's 07/28 run the week
// of 07/20 reads 414K units against 46K of real history — 89% forecast.
//
// engine_manifest.json records `today_proxy`, the date the engine treated as
// today, which is exactly where actuals stop and projection starts.
import { useEffect, useState } from "react";
import { usePerspective } from "../../contexts/app/provider";
import { GetRawParquetDocument } from "../../services/perspective_api/__generated__/perspective_server-types";
import { Run } from "../../services/appsync/types";

export type EngineManifest = {
  /** Epoch ms of the engine's data boundary; null when unavailable. */
  todayProxyMs: number | null;
  loading: boolean;
};

/** Outputs are stored under "{inputId}|{settingsNameAndVersion}". */
const outputSetId = (run: Run): string =>
  `${run.runSettings.inputId}|${run.runSettings.settingsNameAndVersion}`;

export const useEngineManifest = ({
  run,
}: {
  run: Run | null;
}): EngineManifest => {
  const { perspectiveApiService } = usePerspective();
  const [state, setState] = useState<EngineManifest>({
    todayProxyMs: null,
    loading: false,
  });
  const setId = run ? outputSetId(run) : null;

  useEffect(() => {
    if (!setId) {
      setState({ todayProxyMs: null, loading: false });
      return;
    }
    let cancelled = false;
    setState({ todayProxyMs: null, loading: true });
    const client = perspectiveApiService.getClient();
    client
      .query({
        query: GetRawParquetDocument,
        variables: {
          datasetName: "engine_manifest",
          folder: "outputs",
          setId,
          format: "json",
        },
      })
      .then(async (result) => {
        const url = result.data?.getRawParquet;
        if (!url || typeof url !== "string") {
          throw new Error("no manifest");
        }
        const manifest = await fetch(url).then((r) => r.json());
        const raw = manifest?.today_proxy;
        const ms = raw ? new Date(raw).getTime() : NaN;
        if (!cancelled) {
          setState({
            todayProxyMs: Number.isNaN(ms) ? null : ms,
            loading: false,
          });
        }
      })
      .catch(() => {
        // Older runs may predate the manifest; the caller falls back to the
        // run's own timestamp rather than blocking the page.
        if (!cancelled) {
          setState({ todayProxyMs: null, loading: false });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [setId, perspectiveApiService]);

  return state;
};
